import {
    Box,
    Button,
    CircularProgress,
    IconButton,
    Stack,
    SvgIcon,
    Typography
} from '@mui/material';
import React, { useEffect, useState, useRef, useCallback } from 'react';
import Slider from 'react-slick';
import { getTimeDifferenceString } from 'utils/Utils';
import { useNavigate } from 'react-router-dom';
import { getBasicProfileDetails } from 'api/users';
import { useAuth } from 'hooks/useAuthContext';
import { useSnackbar } from 'components/snackbar';
import { Worker, Viewer, SpecialZoomLevel } from '@react-pdf-viewer/core';
import TjResumePreview from 'components/resume-formats/TjResumePreview';
import { updateResumeUrl } from 'api/users';

const settings = {
    dots: false,
    infinite: false,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
};

/**
 * This component is used to select a resume from the list of resumes.
 * It shows a preview of the resume and provides options to update or replace the resume.
 * If the resume is uploaded, it shows a preview of the uploaded resume.
 * If the resume is not uploaded, it shows a preview of the resume in the format of talent junction style.
 * @param {Object} props - The component props.
 * @param {Function} props.onSelect - The function to call when the user selects a resume.
 * @param {Array} props.resumeData - The list of resumes to select from.
 * @param {String} props.tab - The tab to show the resume preview in.
 * @param {Object} props.job - The job associated with the resume.
 * @param {Object} props.answers - The answers associated with the resume.
 * @param {Object} props.chosenResume - The resume to show in the preview.
 * @param {Function} props.onClose - The function to call when the user closes the dialog.
 * @param {Array} props.messages - The messages to show in the preview.
 * @param {Object} props.candidateDetails - The candidate details associated with the resume.
 * @param {Number} props.totalExpectedCompensation - The total expected compensation associated with the resume.
 * @param {Boolean} props.disableUpdate - Whether to disable the update button.
 * @param {Function} props.fetchResumeData - The function to call to fetch the resume data.
 */
const SelectResumeMobile = ({
    fetchResumeData,
    onSelect,
    resumeData,
    selectedResume,
    onClose,
    tab,
    job,
    index,
    answers,
    chosenResume,
    messages,
    candidateDetails,
    totalExpectedCompensation,
    disableUpdate,
    currentFixed,
    currentVariable,
    currentPerks,
    expectedHourlyRate,
    totalCurrentCTC
}) => {
    const { enqueueSnackbar } = useSnackbar();
    const { setJobApply } = useAuth();
    const fileInputRef = useRef(null);
    const sliderRef = useRef(null);
    const navigate = useNavigate();

    const [profile, setProfile] = useState({});
    const [loading, setLoading] = useState(false);

    //  const resume = useMemo(() => {
    //     let targetResume = resumeData.find(item => item?._id === chosenResume?._id);
    //     if (targetResume) {
    //         return targetResume;
    //     }
    //     targetResume = resumeData.find(item => item?._id === selectedResume?._id);
    //     if (targetResume) {
    //         return targetResume;
    //     }
    //     if (chosenResume) return chosenResume;
    //     if (selectedResume) return selectedResume;

    //     return resumeData?.[0] || null;

    // }, [resumeData, chosenResume, selectedResume]);

    // const totalResume = resumeData.length;

    // const title = useMemo(() =>
    //     resume?.link ? 'Uploaded Resume' : resume?.title || 'Resume',
    //     [resume]
    // );

    // const updatedAtText = useMemo(() =>
    //     (resume?.last_updated_at || resume?.updatedAt) ? getTimeDifferenceString(resume?.last_updated_at || resume.updatedAt, 'Updated') : '',
    //     [resume]
    // );

    useEffect(() => {
        const fetchData = async () => {
            try {
                const profileResponse = await getBasicProfileDetails();
                if (profileResponse?.success && profileResponse?.data?.candidate) {
                    setProfile(profileResponse.data.candidate);
                } else {
                    enqueueSnackbar(profileResponse?.message || 'Failed to fetch profile details.', { variant: 'error' });
                }
            } catch (error) {
                console.error('Error fetching data:', error);
                enqueueSnackbar('Failed to fetch data. Please try again later.', { variant: 'error' });
            }
        };
        fetchData();
    }, []);

    useEffect(() => {
        const interval = setInterval(() => {
            if (sliderRef?.current?.innerSlider) {
                sliderRef.current.slickGoTo(index);
                clearInterval(interval);
            } else {
                console.log("Waiting for innerSlider to initialize...");
            }
        }, 100);

        return () => clearInterval(interval);
    }, [index]);

    const handleSelect = (item, index) => {
        onSelect(item, index);
    };

    const handleNavigation = (item, index) => {
        setJobApply({
            job, openChatBot: true, resume: selectedResume, answer: answers, tab, messages, candidateDetails,
            totalExpectedCompensation, disableUpdate, chosenResume: item, openSelectResume: true, index,
            partTimeContinueFlag: true, continueFlag: true,
            currentFixed,
            currentVariable,
            currentPerks, hourlyRate: expectedHourlyRate,
            totalCurrentCTC
        });
        const data = item;
        const totalResume = resumeData?.length;
        if (item?.link) navigate('/create-update-resume', { state: { data, totalResume, tab, jobData: job, uploadResume: true } });
        else navigate('/create-update-resume', { state: { data, totalResume, tab, jobData: job } });
        localStorage.setItem('openChatBot', true);
    };

    const handleNavigateToResume = () => {
        setJobApply({
            job, openChatBot: true, resume: selectedResume, answer: answers, tab, messages, candidateDetails,
            totalExpectedCompensation, disableUpdate, openSelectResume: true,
            index: resumeData?.length,
            partTimeContinueFlag: true, continueFlag: true,
            currentFixed,
            currentVariable,
            currentPerks, hourlyRate: expectedHourlyRate,
            totalCurrentCTC
        });
        localStorage.setItem('openChatBot', true);
        const isResume = resumeData?.find(item => item.title === 'Master Resume');
        if (!isResume) navigate('/create-update-resume', { state: { isMaster: true } });
        else navigate('/create-update-resume', { state: { totalResume: resumeData?.length, createNewResume: true } });
    };

    const handleUploadResume = () => {
        fileInputRef.current.click()
    };

    const handleFileChange = async (e) => {
        if (!e.target.files || e.target.files.length === 0) {
            enqueueSnackbar("No file selected", { variant: "error" });
            return;
        };
        const file = e.target.files[0];
        if (file.size > 2 * 1024 * 1024) {
            enqueueSnackbar("File size must be less than 2 MB. Please upload a smaller file", { variant: "error" });
            return;
        }
        setLoading(true);
        try {
            const formData = new FormData();
            formData.append("file", file);
            const response = await updateResumeUrl(formData);
            if (response?.success && response?.data) {
                fetchResumeData();
            } else {
                enqueueSnackbar("Failed to update resume", { variant: "error" });
                return;
            }
        }
        catch (error) {
            console.error("Error uploading file:", error);
            enqueueSnackbar("Error uploading file. Please try again.", { variant: "error" });
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <Box className="select-resume-options-container">
                <Box className="resume-options-slider-container">
                    <CircularProgress size={24} />
                </Box>
            </Box>
        );
    }

    return (
        <Box className="sidebar-wrapper">
            <input
                type="file"
                accept=".pdf, .doc, .docx, application/msword, application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                ref={fileInputRef}
                onChange={handleFileChange}
                style={{ display: 'none' }}
            />
            <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.4.120/build/pdf.worker.min.js" />

            <Slider {...settings} ref={sliderRef}>
                {resumeData?.map((item, index) => (
                    <Box className="sidebar-wrapper" key={item._id || index}>
                        <Box className="sidebar-header">
                            <Stack direction="row" alignItems="center" spacing={2}>
                                <IconButton sx={{ p: 0 }} onClick={onClose}>
                                    <SvgIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                            <path d="M6.228 5.045a.75.75 0 0 0-1.183.955l5.772 5.772-5.772 5.773a.75.75 0 0 0 1.061 1.06l5.772-5.772 5.773 5.772a.75.75 0 1 0 1.06-1.06l-5.772-5.773 5.772-5.772a.75.75 0 0 0-1.06-1.06l-5.773 5.772L6.228 5.045z" fill="white" />
                                        </svg>
                                    </SvgIcon>
                                </IconButton>

                                <Stack sx={{ minWidth: '100%' }}>
                                    <Typography className="title">
                                        {item?.link ? 'Uploaded Resume' : item?.title}
                                    </Typography>
                                    {/* {updatedAtText && <Typography className="into-text">{updatedAtText}</Typography>} */}
                                    <Typography className="into-text">
                                        {(item?.last_updated_at || item?.updatedAt)
                                            ? getTimeDifferenceString(item.last_updated_at || item.updatedAt, "Updated") :
                                            getTimeDifferenceString(profile?.last_updated_at || profile?.updatedAt, "Updated")}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Box>

                        <Box className="sidebar-content" sx={{ p: '0!important', width: '100%' }}>
                            {item?.link ? (
                                <div style={{ width: '100%', height: '75vh' }}>
                                    <Viewer
                                        fileUrl={process.env.REACT_APP_END_POINT + item?.link}
                                        defaultScale={SpecialZoomLevel.PageWidth}
                                    />
                                </div>
                            ) :
                                <TjResumePreview resume={item} />
                            }
                        </Box>

                        <Box className="sidebar-footer">
                            <Stack direction="row" spacing={2}>
                                <Button
                                    fullWidth
                                    size="large"
                                    className="cancel-btn"
                                    onClick={() => item?.link ? handleUploadResume(item) : handleNavigation(item, index)}>
                                    {item?.link ? 'Replace' : 'Update'}
                                </Button>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    size="large"
                                    className="update-btn"
                                    onClick={() => handleSelect(item, index)}>
                                    Select Resume
                                </Button>
                            </Stack>
                        </Box>
                    </Box>
                ))}
                {resumeData?.length < 5 && (
                    <Box className="resume-option-item last-slide">
                        <Box className="resume-options-preview" >
                            <Box className="create-new-resume-slide" sx={{ height: '85vh !important' }}>
                                <Box className="inner-container" onClick={handleNavigateToResume}>
                                    <SvgIcon>
                                        <svg xmlns="http://www.w3.org/2000/svg" width="74" height="88" viewBox="0 0 74 88" fill="none">
                                            <path fillRule="evenodd" clipRule="evenodd" d="M8.35059 0.196289C3.93231 0.196289 0.350586 3.77801 0.350586 8.19629V74.1963C0.350586 78.6146 3.93231 82.1963 8.35059 82.1963H41.0889C40.6326 81.5543 40.2038 80.8927 39.8271 80.1963H10.3506C5.93231 80.1963 2.35059 76.6146 2.35059 72.1963V10.1963C2.35059 5.77801 5.93231 2.19629 10.3506 2.19629H52.3506C56.7689 2.19629 60.3506 5.77801 60.3506 10.1963V51.9912C61.0299 52.1154 61.6978 52.2704 62.3506 52.4638V8.19628C62.3506 3.778 58.7689 0.196289 54.3506 0.196289H8.35059ZM15.5127 48.0078V50.0059H47.1904V48.0078H15.5127ZM15.5127 57.8173V55.8193H44.915C44.1405 56.4295 43.418 57.0981 42.7451 57.8173H15.5127ZM25.3252 29.5264V29.7432V35.7705H37.3779V29.5264H35.3818V33.7725H27.3213V29.5264H25.3252ZM27.8389 26.7021C27.8389 24.7727 29.4211 23.1904 31.3506 23.1904C33.2801 23.1904 34.8643 24.7727 34.8643 26.7021C34.8643 28.6316 33.2799 30.2139 31.3506 30.2139C29.4212 30.2139 27.8389 28.6316 27.8389 26.7021ZM32.8662 26.7021C32.8662 25.8509 32.2018 25.1865 31.3506 25.1865C30.4993 25.1865 29.835 25.8509 29.835 26.7021C29.835 27.5534 30.4991 28.2178 31.3506 28.2178C32.2021 28.2178 32.8662 27.5534 32.8662 26.7021ZM31.3506 16.7646C24.3397 16.7646 18.6357 22.4686 18.6357 29.4795C18.6357 36.4904 24.3397 42.1943 31.3506 42.1943C38.3615 42.1943 44.0674 36.4904 44.0674 29.4795C44.0674 22.4686 38.3615 16.7646 31.3506 16.7646ZM31.3506 18.7607C37.2822 18.7607 42.0693 23.5479 42.0693 29.4795C42.0693 35.4111 37.2822 40.1982 31.3506 40.1982C25.419 40.1982 20.6338 35.4111 20.6338 29.4795C20.6338 23.5479 25.419 18.7607 31.3506 18.7607ZM15.5127 65.628V63.6299H38.9795C38.7106 64.2782 38.4842 64.9467 38.2861 65.628H15.5127ZM50.4399 55.5089C52.4767 54.6653 54.6597 54.2313 56.8643 54.2314C59.0687 54.2315 61.2515 54.6658 63.288 55.5095C65.3246 56.3531 67.175 57.5897 68.7337 59.1486C70.2923 60.7074 71.5287 62.558 72.3721 64.5947C73.2156 66.6313 73.6496 68.8142 73.6494 71.0186C73.6492 75.4703 71.8808 79.7395 68.733 82.8873C65.5852 86.035 61.3159 87.8035 56.8643 87.8036C54.6599 87.8038 52.477 87.3699 50.4404 86.5264C48.4037 85.683 46.5531 84.4466 44.9943 82.888C43.4354 81.3294 42.1989 79.4789 41.3552 77.4423C40.5115 75.4058 40.0772 73.223 40.0771 71.0186C40.077 68.814 40.5111 66.631 41.3546 64.5942C42.1982 62.5574 43.4347 60.7068 44.9936 59.1479C46.5524 57.589 48.4031 56.3525 50.4399 55.5089ZM57.8643 70.0165V63.5165H55.8643V70.0165H49.3643V72.0165H55.8643V78.5165H57.8643V72.0165H64.3643V70.0165H57.8643Z" fill="#1575C6" />
                                        </svg>
                                    </SvgIcon>
                                    <Typography>Create new resume and select</Typography>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                )}
            </Slider>
        </Box>
    );
};

export default SelectResumeMobile;
